import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, ShieldCheck, User as UserIcon, Eye } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Yönetici" },
  { value: "editor", label: "Düzenleyen" },
  { value: "viewer", label: "Görüntüleyen" },
];
const roleLabel = (r) => (ROLES.find((x) => x.value === r) || { label: r }).label;

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/users"); setRows(res.data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) { toast.error("E-posta giriniz"); return; }
    try {
      await api.post("/users", { email, role });
      toast.success("Kullanıcı eklendi");
      setEmail(""); setRole("viewer"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const changeRole = async (em, newRole) => {
    try {
      await api.put(`/users/${encodeURIComponent(em)}/role`, { email: em, role: newRole });
      toast.success(`Rol güncellendi: ${roleLabel(newRole)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const remove = async (em) => {
    try {
      await api.delete(`/users/${encodeURIComponent(em)}`);
      toast.success("Erişim kaldırıldı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <Layout title="Kullanıcı Yönetimi" subtitle="Sisteme giriş yapabilecek onaylı e-postalar">
      <Card className="rounded-sm border border-border p-6 bg-white mb-4">
        <h3 className="font-display font-bold text-lg mb-1">Yeni Kullanıcı Ekle</h3>
        <p className="text-xs text-muted-foreground mb-4">Yalnızca eklediğiniz e-postalar Google ile giriş yapabilir. <strong>Yönetici</strong>: tam yetki + kullanıcı/ayar yönetimi · <strong>Düzenleyen</strong>: kayıt ekleme/düzenleme/silme · <strong>Görüntüleyen</strong>: yalnızca okuma.</p>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">E-posta</Label>
            <Input data-testid="user-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kisi@rayemdemiryolu.com" className="rounded-sm" />
          </div>
          <div className="w-full sm:w-48 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Rol</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="user-role-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="add-user-btn" onClick={add} className="rounded-sm gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto">
            <UserPlus className="w-4 h-4" /> Ekle
          </Button>
        </div>
      </Card>

      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">E-posta</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Rol</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Durum</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && rows.map((r) => (
              <TableRow key={r.email} data-testid={`user-row-${r.email}`} className="odd:bg-white even:bg-zinc-50/50">
                <TableCell className="font-medium">{r.email}{r.is_you && <span className="ml-2 text-xs text-muted-foreground">(siz)</span>}</TableCell>
                <TableCell>
                  {r.email === "bariscanyildiz@rayemdemiryolu.com" ? (
                    <Badge className="rounded-sm bg-secondary text-white gap-1"><ShieldCheck className="w-3 h-3" /> Yönetici</Badge>
                  ) : (
                    <Select value={r.role} onValueChange={(v) => changeRole(r.email, v)}>
                      <SelectTrigger data-testid={`role-select-${r.email}`} className="rounded-sm h-8 w-40 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {r.has_logged_in
                    ? <span className="text-xs text-emerald-700 font-medium">Giriş yaptı</span>
                    : <span className="text-xs text-muted-foreground">Bekliyor</span>}
                </TableCell>
                <TableCell className="text-right">
                  {r.is_you || r.email === "bariscanyildiz@rayemdemiryolu.com" ? (
                    <span className="text-xs text-muted-foreground pr-3">—</span>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`remove-user-${r.email}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Erişim kaldırılsın mı?</AlertDialogTitle>
                          <AlertDialogDescription>{r.email} artık sisteme giriş yapamayacak ve aktif oturumu kapatılacak.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
                          <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(r.email)}>Kaldır</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
